import { useState } from "react";
import { Loader2, Lock, Smartphone, CreditCard, Landmark, Wallet, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { fmtDate } from "@/lib/api";

const PAY_ICONS = [
  { icon: Smartphone, text: "UPI" }, { icon: Wallet, text: "GPay" }, { icon: Wallet, text: "PhonePe" },
  { icon: CreditCard, text: "Card" }, { icon: Landmark, text: "Netbanking" },
];

const validate = (f) => {
  const e = {};
  if (f.patient_name.trim().length < 2) e.patient_name = "Please enter your full name";
  if (!/^[6-9]\d{9}$/.test(f.patient_phone)) e.patient_phone = "Enter a valid 10-digit mobile number";
  if (f.patient_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.patient_email)) e.patient_email = "Enter a valid email or leave it blank";
  return e;
};

const Field = ({ label, error, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-ink">{label}</span>
    <div className="mt-1">{children}</div>
    {error && <span className="text-xs text-red-600 mt-1 block" data-testid="field-error">{error}</span>}
  </label>
);

export const DetailsStep = ({ form, setForm, reason, date, slot, fee, paying, onPay, onEdit }) => {
  const [errors, setErrors] = useState({});
  const set = (k) => (e) => setForm({ ...form, [k]: k === "patient_phone" ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value });
  const submit = (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (!Object.keys(errs).length) onPay();
  };

  return (
    <form onSubmit={submit} noValidate data-testid="details-step">
      <p className="text-sm font-semibold text-ink mb-3">Step 3 · Your details</p>
      <div className="rounded-2xl bg-mint/60 border border-mint-border p-3.5 text-sm mb-4 flex justify-between gap-3" data-testid="booking-summary">
        <div className="min-w-0">
          <p className="font-medium text-ink truncate">{reason?.title}</p>
          <p className="text-teal">{fmtDate(date)} · {slot?.label}</p>
        </div>
        <button type="button" onClick={() => onEdit(2)} className="text-xs text-teal font-medium hover:underline shrink-0" data-testid="summary-edit-button">Change</button>
      </div>
      <div className="space-y-3">
        <Field label="Full name *" error={errors.patient_name}>
          <Input value={form.patient_name} onChange={set("patient_name")} placeholder="e.g. Priya Sharma" className="h-11 rounded-xl" data-testid="patient-name-input" autoComplete="name" />
        </Field>
        <Field label="Mobile / WhatsApp number *" error={errors.patient_phone}>
          <div className="flex h-11 rounded-xl border border-input overflow-hidden focus-within:ring-2 focus-within:ring-ring">
            <span className="px-3 grid place-items-center bg-slate-50 text-sm text-ink-muted border-r">+91</span>
            <input value={form.patient_phone} onChange={set("patient_phone")} inputMode="numeric" placeholder="10-digit number" autoComplete="tel-national"
              className="flex-1 px-3 text-sm outline-none bg-transparent" data-testid="patient-phone-input" />
          </div>
        </Field>
        <Field label="Email (optional)" error={errors.patient_email}>
          <Input type="email" value={form.patient_email} onChange={set("patient_email")} placeholder="you@example.com" className="h-11 rounded-xl" data-testid="patient-email-input" autoComplete="email" />
        </Field>
        <Field label="Brief note / symptom (optional)">
          <Textarea value={form.notes} onChange={set("notes")} maxLength={500} rows={2} placeholder="e.g. Sensitivity on the lower left side" className="rounded-xl" data-testid="patient-notes-input" />
        </Field>
      </div>
      <div className="mt-5 rounded-2xl border border-slate-200 p-4" data-testid="fee-summary">
        <div className="flex items-center justify-between">
          <span className="text-sm text-ink">In-clinic consultation &amp; diagnosis</span>
          <span className="font-display text-xl font-semibold text-teal" data-testid="fee-amount">₹{fee}</span>
        </div>
        <p className="mt-2 flex gap-2 text-xs text-ink-muted" data-testid="fee-note">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-teal" />
          ₹{fee} covers complete clinical examination &amp; diagnosis by Dr. Vilayath Mahamadi. Further treatment costs are personalized based on your diagnosis.
        </p>
      </div>
      <button type="submit" disabled={paying} data-testid="pay-confirm-button"
        className="mt-5 w-full h-13 py-3.5 rounded-full bg-teal text-white font-semibold text-base inline-flex items-center justify-center gap-2 hover:bg-teal-dark disabled:opacity-60 transition-colors shadow-[0_12px_24px_-12px_rgba(15,76,92,0.8)]">
        {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-4 w-4" />} Pay ₹{fee} &amp; Confirm Slot
      </button>
      <div className="mt-3 flex flex-wrap justify-center gap-1.5" data-testid="payment-trust-icons">
        {PAY_ICONS.map(({ icon: I, text }) => (
          <span key={text} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-ink-muted"><I className="h-3 w-3" />{text}</span>
        ))}
      </div>
      <p className="mt-2 text-center text-[11px] text-ink-muted">Your slot is held for 10 minutes while you pay. Secured by Razorpay.</p>
    </form>
  );
};
